import { useMutation, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import React, { useState } from 'react';
import { View } from 'react-native';
import {
  Button,
  DataTable,
  Dialog,
  Portal,
  Searchbar,
  Text,
} from 'react-native-paper';
import useConfirmModal from 'shared/hooks/useConfirmModal';
import useDebounceState from 'shared/hooks/useDebounceState';
import { UserDisplay } from 'shared/types';

interface Props {
  visible: boolean;
  resultId: string;
  trackId: string;
  userDisplay?: UserDisplay;
  onDismiss: () => void;
  onSelectUser?: () => void;
}

const GET_TRACK_USERS = gql`
  query GetTrackUsers(
    $trackId: ID!
    $offset: Int
    $search: String
    $userDisplay: Int
  ) {
    track(id: $trackId) {
      id
    }
    users(
      offset: $offset
      trackId: $trackId
      search: $search
      sort: "name.asc"
    ) {
      id
      name(display: $userDisplay)
      compRegistration(trackId: $trackId) {
        id
        division {
          name
        }
      }
    }
    usersCount(trackId: $trackId, search: $search)
  }
`;

const GET_WORKOUT_RESULT = gql`
  query GetWorkoutResult($id: ID!, $userDisplay: Int) {
    workoutResult(id: $id) {
      id
      user {
        id
        name(display: $userDisplay)
      }
    }
  }
`;

const TRANSFER_RESULT_MUTATION = gql`
  mutation TransferResult($resultId: ID!, $userId: ID!, $userDisplay: Int) {
    transferResult(resultId: $resultId, userId: $userId) {
      id
      user {
        id
        name(display: $userDisplay)
      }
    }
  }
`;

export default function TransferResultModal({
  visible,
  resultId,
  trackId,
  userDisplay,
  onDismiss,
  onSelectUser,
}: Props) {
  const [search, setSearch, unbouncedSearch] = useDebounceState('');
  const [page, setPage] = useState(1);
  const [transferringUserId, setTransferringUserId] = useState<string | null>(
    null
  );
  const limit = 10;
  const confirmWith = useConfirmModal();

  const { data, loading: usersLoading } = useQuery(GET_TRACK_USERS, {
    variables: {
      trackId,
      offset: (page - 1) * limit,
      search: search || null,
      userDisplay,
    },
    skip: !trackId,
    fetchPolicy: 'cache-and-network',
  });

  const { data: resultData, loading: resultLoading } = useQuery(
    GET_WORKOUT_RESULT,
    {
      variables: {
        id: resultId,
        userDisplay,
      },
      skip: !resultId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const [transferResult, { loading: transferring }] = useMutation(
    TRANSFER_RESULT_MUTATION
  );

  const users = (data && data.users) || [];
  const usersCount = (data && (data as any).usersCount) || 0;
  const originalUser =
    resultData && resultData.workoutResult
      ? resultData.workoutResult.user
      : null;
  const originalUserName = originalUser ? originalUser.name : null;

  const handleTransfer = (user: any) => {
    confirmWith({
      title: 'Confirm Transfer',
      message: `Are you sure you want to transfer this result from ${originalUserName} to ${user.name}?`,
      confirmLabel: 'Transfer Result',
      onConfirm: () => {
        setTransferringUserId(user.id);
        transferResult({
          variables: {
            resultId,
            userId: user.id,
            userDisplay,
          },
        })
          .then(() => {
            if (onSelectUser) {
              onSelectUser();
            }
            onDismiss();
          })
          .catch(({ graphQLErrors }) => {
            const message =
              graphQLErrors &&
              graphQLErrors.map((e: any) => e.message).join(', ');
            setTimeout(() => {
              confirmWith({
                title: 'Transfer Failed',
                message:
                  message || 'Failed to transfer result. Please try again.',
                hideCancel: true,
              });
            }, 500);
          })
          .finally(() => {
            setTransferringUserId(null);
          });
      },
    });
  };

  return (
    <Portal>
      <Dialog
        visible={visible}
        onDismiss={onDismiss}
        style={{ width: '60vw', maxWidth: 800, alignSelf: 'center' }}
      >
        <Dialog.Title>
          Transfer Result {originalUserName ? `from ${originalUserName}` : ''}
        </Dialog.Title>
        <Dialog.Content>
          <View style={{ marginBottom: 8 }}>
            <Searchbar
              placeholder="Filter by name"
              value={unbouncedSearch}
              onChangeText={setSearch}
            />
          </View>
          <DataTable>
            <DataTable.Header>
              <DataTable.Title>Name</DataTable.Title>
              <DataTable.Title>Division</DataTable.Title>
              <DataTable.Title style={{ flex: 0.4 }} numeric>
                Action
              </DataTable.Title>
            </DataTable.Header>
            {users.map((user: any) => (
              <DataTable.Row key={user.id}>
                <DataTable.Cell>{user.name}</DataTable.Cell>
                <DataTable.Cell>
                  {user.compRegistration && user.compRegistration.division
                    ? user.compRegistration.division.name
                    : ''}
                </DataTable.Cell>
                <DataTable.Cell style={{ flex: 0.4 }} numeric>
                  {originalUser && user.id === originalUser.id ? (
                    <Text>Original User</Text>
                  ) : (
                    <Button
                      compact
                      mode="outlined"
                      loading={transferringUserId === user.id}
                      disabled={usersLoading || resultLoading || transferring}
                      onPress={() => handleTransfer(user)}
                    >
                      Transfer
                    </Button>
                  )}
                </DataTable.Cell>
              </DataTable.Row>
            ))}
            <DataTable.Pagination
              page={page}
              numberOfPages={Math.ceil(usersCount / limit) + 1}
              onPageChange={(newPage) => {
                if (newPage === 0) {
                  setPage(1);
                } else {
                  setPage(newPage);
                }
              }}
              label={`Page ${page}`}
            />
            {!usersLoading && users.length === 0 && (
              <View style={{ padding: 16 }}>
                <Text>No users found.</Text>
              </View>
            )}
          </DataTable>
        </Dialog.Content>
      </Dialog>
    </Portal>
  );
}
